<template>
    <el-card class="main-card">
        <template #header>
            <el-breadcrumb separator-class="el-icon-arrow-right">
                <el-breadcrumb-item :to="{ path: '/' }">首页</el-breadcrumb-item>
                <el-breadcrumb-item :to="{ path: '/sys/message' }">消息列表</el-breadcrumb-item>
                <el-breadcrumb-item>消息详情</el-breadcrumb-item>
            </el-breadcrumb>
        </template>

        <div v-loading="pageLoading" class="message-detail-page">
            <el-tabs v-model="activeTab" @tab-change="handleTabChange">
                <!-- Tab 1: 消息详情 -->
                <el-tab-pane label="消息详情" name="detail">
                    <el-descriptions v-if="currentMessage" :column="2" border label-width="130px">
                        <el-descriptions-item label="消息标题" :span="2">
                            {{ currentMessage.title }}
                        </el-descriptions-item>
                        <el-descriptions-item label="副标题" :span="2">
                            {{ currentMessage.subTitle }}
                        </el-descriptions-item>
                        <el-descriptions-item label="消息类型">
                            {{ currentMessage.category }}
                        </el-descriptions-item>
                        <el-descriptions-item label="创建时间">
                            {{ currentMessage.createDate }}
                        </el-descriptions-item>
                        <el-descriptions-item label="消息来源">
                            {{ currentMessage.sourceUserName }}
                        </el-descriptions-item>
                        <el-descriptions-item label="接收用户">
                            {{ currentMessage.targetUserName }}
                        </el-descriptions-item>
                        <el-descriptions-item label="待办状态">
                            <el-tag v-if="currentMessage.done === true" type="success">已处理</el-tag>
                            <el-tag v-else-if="currentMessage.done === false" type="warning">待处理</el-tag>
                            <el-tag v-else type="info">无需处理</el-tag>
                        </el-descriptions-item>
                        <el-descriptions-item label="阅读状态" :span="2">
                            <el-tag v-if="currentMessage.seen === true" type="success">已阅</el-tag>
                            <el-tag v-else-if="currentMessage.seen === false" type="warning">待阅</el-tag>
                            <el-tag v-else type="info">无需阅读</el-tag>
                        </el-descriptions-item>
                        <el-descriptions-item label="备注" :span="2">
                            {{ currentMessage.remark || '无' }}
                        </el-descriptions-item>
                    </el-descriptions>
                    <div v-else class="no-message">
                        <el-empty description="未找到消息详情"></el-empty>
                    </div>
                </el-tab-pane>

                <!-- Tab 2: 动态网页 -->
                <el-tab-pane label="相关页面" name="webpage" v-if="currentMessage" :disabled="!currentMessage.pageUrl">
                    <div v-loading="webpageLoading" class="webpage-container">
                        <iframe v-if="currentMessage && currentMessage.pageUrl" v-show="activeTab === 'webpage'"
                            :src="getPageUrl()" style="width: 100%; height: 600px; border: none;">
                        </iframe>
                        <div v-if="!currentMessage || !currentMessage.pageUrl" class="no-webpage">
                            <el-empty description="该消息没有关联页面"></el-empty>
                        </div>
                    </div>
                </el-tab-pane>

                <!-- Tab 3: 审核操作 -->
                <el-tab-pane label="审核操作" name="audit" v-if="currentMessage"
                    :disabled="!currentMessage.apiUrl && !flowdata">
                    <div v-loading="auditLoading" class="audit-container">
                        <div v-if="currentMessage && currentMessage.apiUrl && currentMessage.done !== true">
                            <el-alert title="审核提示" type="info" description="请仔细核查相关信息后进行审核操作，操作后将无法撤销。"
                                :closable="false" style="margin-bottom: 20px;">
                            </el-alert>

                            <el-form ref="auditForm" :model="auditForm" :rules="auditRules" label-width="100px">
                                <el-form-item label="审核结果" prop="result">
                                    <el-radio-group v-model="auditForm.result">
                                        <el-radio :label="true">通过</el-radio>
                                        <el-radio :label="false">拒绝</el-radio>
                                    </el-radio-group>
                                </el-form-item>
                                <el-form-item label="审核意见" prop="comment">
                                    <el-input v-model="auditForm.comment" type="textarea" :rows="4"
                                        placeholder="请输入审核意见（必填）" maxlength="500" show-word-limit>
                                    </el-input>
                                </el-form-item>
                            </el-form>

                            <div class="audit-actions">
                                <el-button @click="resetAuditForm">重置</el-button>
                                <el-button type="primary" @click="submitAudit" :loading="auditLoading">
                                    提交审核
                                </el-button>
                                <el-button @click="goBack">返回列表</el-button>
                            </div>
                        </div>
                        <div v-else-if="currentMessage && currentMessage.done === true" class="audit-completed">
                            <el-result icon="success" title="审核已完成" sub-title="该消息已经完成审核处理">
                                <template #extra>
                                    <el-button type="primary" @click="goBack">返回列表</el-button>
                                </template>
                            </el-result>
                        </div>
                        <div v-else class="no-audit">
                            <el-empty description="该消息无需审核">
                                <template #extra>
                                    <el-button type="primary" @click="goBack">返回列表</el-button>
                                </template>
                            </el-empty>
                        </div>
                    </div>
                </el-tab-pane>

                <el-tab-pane label="流程详情" name="flowdata" v-if="flowdata">
                    <el-descriptions v-if="flowdata" :column="2" border label-width="130px">
                        <el-descriptions-item label="流程名称" :span="2">
                            {{ flowdata.flowName }}
                        </el-descriptions-item>
                        <el-descriptions-item label="流程描述" :span="2">
                            {{ flowdata.formDesc }}
                        </el-descriptions-item>
                        <el-descriptions-item label="当前节点">
                            {{ flowdata.nodeName }}
                        </el-descriptions-item>
                        <el-descriptions-item label="发起时间">
                            {{ flowdata.createDate }}
                        </el-descriptions-item>
                        <el-descriptions-item label="待办人员">
                            {{ flowdata.operatorName }}
                        </el-descriptions-item>
                        <el-descriptions-item label="单据编号">
                            {{ flowdata.billCode }}
                        </el-descriptions-item>
                        <el-descriptions-item label="公司名称">
                            {{ flowdata.companyName }}
                        </el-descriptions-item>
                        <el-descriptions-item label="部门名称">
                            {{ flowdata.departmentName }}
                        </el-descriptions-item>
                        <el-descriptions-item label="待办状态">
                            <el-tag v-if="flowdata.status === 0" type="info">草稿</el-tag>
                            <el-tag v-else-if="flowdata.status === 10" type="primary">流转中</el-tag>
                            <el-tag v-else-if="flowdata.status === 50" type="success">已完成</el-tag>
                            <el-tag v-else-if="flowdata.status === -10" type="warning">被退回</el-tag>
                            <el-tag v-else-if="flowdata.status === -50" type="danger">被拒绝</el-tag>
                        </el-descriptions-item>
                    </el-descriptions>
                    <div v-else class="no-message">
                        <el-empty description="未找到消息详情"></el-empty>
                    </div>
                </el-tab-pane>
                <el-tab-pane name="flowdraw" v-if="drawdata">
                    <template #label>
                        <span> 审批流程图 </span>
                    </template>

                    <div id="flowdraw">
                        <drawapp ref="drawapp1" :data="drawdata" :height="500" :width="800" />
                    </div>
                </el-tab-pane>
            </el-tabs>
        </div>
    </el-card>
</template>

<script>
import drawapp from "@/components/flowDraw";
import { getInstance } from "@/api/workflow";
export default {
    name: "MessageDetail",
    components: { drawapp },
    data() {
        return {
            pageLoading: false,
            activeTab: 'detail',
            currentMessage: null,
            cachedPageUrl: '',
            webpageLoading: false,
            auditLoading: false,

            // 审核表单
            auditForm: {
                result: null,
                comment: ''
            },
            auditRules: {
                result: [
                    { required: true, message: '请选择审核结果', trigger: 'change' }
                ],
                comment: [
                    { required: true, message: '请输入审核意见', trigger: 'blur' },
                    { min: 5, message: '审核意见至少5个字符', trigger: 'blur' }
                ]
            },

            drawdata: null,
            flowdata: null,
        };
    },
    mounted() {
        this.$kaung.init('BaseMessage');
        this.loadMessageDetail();
    },
    methods: {
        loadMessageDetail() {
            const messageId = this.$route.params.id;
            if (!messageId) {
                this.$message.error('消息ID参数错误');
                this.goBack();
                return;
            }

            this.pageLoading = true;
            this.$kaung.getone(messageId).then((res) => {
                if (res.code === 200) {
                    this.currentMessage = { ...res.data };
                    this.loadFlowData(this.currentMessage.dataID);
                    this.refreshUnreadMessageCount();
                } else {
                    this.$message.error('加载消息详情失败，未找到该消息');
                    //this.goBack();
                }
            }).catch((error) => {
                console.error('加载消息详情失败:', error);
                //this.$message.error('加载消息详情失败');
                //this.goBack();
            }).finally(() => {
                this.pageLoading = false;
            });
        },

        handleTabChange(tabName) {
            console.log('Tab switched to:', tabName);
        },

        getPageUrl() {
            if (!this.currentMessage || !this.currentMessage.pageUrl) return '';
            return this.currentMessage.pageUrl + '?id=' + this.currentMessage.dataID;
        },

        resetAuditForm() {
            this.auditForm = {
                result: null,
                comment: ''
            };
            if (this.$refs.auditForm) {
                this.$refs.auditForm.clearValidate();
            }
        },

        submitAudit() {
            this.$refs.auditForm.validate(async (valid) => {
                if (!valid) return;

                this.auditLoading = true;

                const auditData = {
                    messageId: this.currentMessage.id,
                    approved: this.auditForm.result,
                    comment: this.auditForm.comment,
                    auditDate: new Date().toISOString()
                };

                const response = await this.$http.post(this.currentMessage.apiUrl, auditData);

                if (response.code === 200) {
                    this.$message({
                        type: 'success',
                        message: '审核操作成功'
                    });

                    // 更新消息状态
                    this.currentMessage.done = true;
                    this.refreshUnreadMessageCount();

                    // 可以选择返回列表或刷新当前页面
                    setTimeout(() => {
                        this.goBack();
                    }, 1500);
                } else {
                    this.$message({
                        type: 'error',
                        message: response.content || '审核操作失败'
                    });
                }
            });
        },

        goBack() {
            this.$router.push('/sys/message');
        },

        loadFlowData(dataID) {
            dataID = dataID || this.$route.params.id;

            //加载已经保存过的数据
            getInstance(dataID).then((res) => {
                if (res.code === 200) {
                    this.flowdata = res.data;

                    this.drawdata = JSON.parse(res.data.flowData);

                    this.activeTab = 'flowdata';
                }
            });
        },
        refreshUnreadMessageCount() {
            this.$store.dispatch('fetchUnreadMessageCount').catch((error) => {
                console.log('刷新未读消息数量失败:', error);
            });
        },
    },
};
</script>

<style scoped>
.message-detail-page {
    min-height: 500px;
}

.message-detail {
    padding: 20px;
}

.webpage-container {
    min-height: 600px;
}

.no-webpage,
.no-audit,
.audit-completed {
    padding: 40px;
    text-align: center;
}

.audit-container {
    padding: 20px;
}

.audit-actions {
    border-top: 1px solid #ebeef5;
    padding-top: 20px;
    text-align: center;
}

.audit-actions .el-button {
    margin: 0 5px;
}
</style>
